import { z } from 'zod';
/**
 * Per-tenant feature entitlements.
 *
 * Three things get conflated in products like this, and keeping them apart is
 * the whole point of the file:
 *
 *  - **Entitlement** — what the customer bought. Platform-controlled, and on a
 *    marketplace it arrives from the marketplace. This is that layer.
 *  - **Preference** — how the tenant chose to configure something they already
 *    have. Tenant-editable, and lives in `TenantSetting` (units, correlation
 *    thresholds).
 *  - **Permission** — whether this particular person may use it. That is
 *    `RolesGuard`, and it is unchanged.
 *
 * A tenant administrator must never be able to switch a feature off, because
 * the control would look like a preference and would actually be disabling
 * something they are paying for. Writes are platform-scoped for that reason.
 *
 * ## Why a registry rather than a column
 *
 * `Tenant.llmEnabled` is a boolean column, which works for one feature and
 * stops working at three. More importantly a column carries no statement of
 * WHAT it gates, so the answer lives scattered across the routes that happen to
 * check it — and a route that forgets is indistinguishable from one that had no
 * need to check.
 *
 * Declaring the surface here makes omission visible: `gates` is the enumerated
 * list of what disappears, and a registry test asserts every entry is really
 * guarded. The same trick `MODEL_CLASSIFICATION` uses to make an unclassified
 * model a build failure rather than an oversight.
 */
export const FEATURE_KEYS = [
    'TELEMETRY',
    'CAN_TELEMETRY',
    'TACHOGRAPH',
    'ANOMALY_DETECTION',
    'ALERTING',
    'WHITE_LABEL',
    'SCHEDULED_REPORTS',
    'AI_COPILOT',
    'AI_EXTERNAL_PROVIDER',
    'REPORT_EXTERNAL_RECIPIENTS',
    'BULK_VEHICLE_IMPORT',
];
export const featureKeySchema = z.enum(FEATURE_KEYS);
/**
 * How the catalogue is GROUPED on a screen, in display order.
 *
 * ⚠️ **Presentation, and deliberately not `scope` or `PLANS`.** `scope` is a
 * hierarchy level and `PLANS` is a commercial bundle; both already exist and
 * neither answers *what kind of thing is this*. A ceiling screen listing ten
 * flat rows is a wall to read, and grouping it by plan would tell a reader
 * which bundle to buy rather than what the feature does.
 *
 * ⚠️ It lives HERE rather than in the web app because two screens already show
 * the catalogue — the partner ceiling and the per-tenant grants — and a grouping
 * held in one of them is a second vocabulary the moment the other wants it.
 */
export const FEATURE_CATEGORIES = [
    'FLEET_DATA',
    'INTELLIGENCE',
    'AUTOMATION',
    'ADMINISTRATION',
];
export const featureCategorySchema = z.enum(FEATURE_CATEGORIES);
/**
 * Wording for each group, kept beside the values so no screen invents its own.
 *
 * `Record<FeatureCategory, …>` rather than a lookup with a fallback: a category
 * added without wording is a COMPILE error, which is the same guarantee
 * `categoryIcons` and `noteTypes` get from being closed.
 */
export const FEATURE_CATEGORY_LABELS = {
    FLEET_DATA: {
        label: 'Fleet data',
        description: 'Core vehicle and driver data from your devices.',
    },
    INTELLIGENCE: {
        label: 'Intelligence',
        description: 'Turn your fleet data into insights and actions.',
    },
    AUTOMATION: {
        label: 'Automation and reporting',
        description: 'Keep people informed with alerts and reports.',
    },
    ADMINISTRATION: {
        label: 'Administration',
        description: 'Branding and bulk administration tools.',
    },
};
/**
 * Split a set of feature keys into the display groups, in declared order.
 *
 * ⚠️ ONE derivation, because it is now on two screens — the partner ceiling and
 * the partner create form — and a third (per-tenant grants) will want it. Two
 * copies would be two ORDERINGS the day somebody reorders `FEATURE_CATEGORIES`,
 * and a reader moving between the two screens would find the same catalogue
 * arranged differently with nothing saying why.
 *
 * ⚠️ An EMPTY group is dropped rather than returned empty. A heading over no rows
 * reads as a rendering fault — the `Vehicle` heading the tachograph card had to
 * learn — and every caller would otherwise have to remember to filter.
 */
export function featuresByCategory(keys) {
    return FEATURE_CATEGORIES.map((category) => ({
        category,
        keys: keys.filter((k) => FEATURES[k].category === category),
    })).filter((g) => g.keys.length > 0);
}
export const FEATURES = {
    TELEMETRY: {
        label: 'Telemetry',
        category: 'FLEET_DATA',
        mandatory: true,
        description: 'Ingest telemetry and see where vehicles are: live fleet positions, a vehicle’s track ' +
            'over the last day, and history for the parameters any tracker reports.',
        /*
         * The four routes that never needed a CAN interface.
         *
         * They were gated on CAN_TELEMETRY until 28 August 2026, which meant a
         * customer with a plain GPS tracker could not open the fleet map: position
         * was bundled with DPF soot load. The tier boundary now sits where the
         * HARDWARE boundary sits — 7 of the 43 canonical parameters come off any
         * tracker, the other 36 need a device wired to the vehicle bus — because
         * that line is physical rather than commercial, and nobody has to argue
         * about which side a parameter falls on.
         */
        gates: [
            'POST /api/v1/providers/:provider/telemetry',
            'GET /api/v1/fleet/positions',
            'GET /api/v1/vehicles/:id/track',
            // The vehicle's journeys. Derived from the same basic parameters — ignition
            // and speed — so it needs no more entitlement than a position does.
            'GET /api/v1/vehicles/:id/trips',
            // Boolean telemetry as state bands. `ignitionOn` is basic tier, so this
            // needs no more than a position does; the CAN booleans simply have no
            // data for a tenant that cannot store them.
            'GET /api/v1/vehicles/:id/telemetry/states',
            // Serves both tiers. A TELEMETRY-only tenant asking for `engineRpm` gets
            // the same answer as one asking for a parameter its vehicles never sent:
            // an empty series, because the samples were never stored (D3).
            'GET /api/v1/vehicles/:id/telemetry',
        ],
        /*
         * No route of its own. Positions appear on the fleet page and the vehicle
         * page, both of which every tenant already has, so listing one here would
         * name a URL that does not exist and the registry test compares this against
         * real routes.
         */
        routes: ['/operations/anomalies'],
        defaultEnabled: false,
        scope: 'TENANT',
        implies: [],
    },
    CAN_TELEMETRY: {
        label: 'CAN telemetry',
        category: 'FLEET_DATA',
        mandatory: false,
        description: 'Ingest CAN bus samples and use everything derived from them: fuel economy, engine ' +
            'condition, driver inputs, fault codes, trends and class comparison.',
        gates: [
            'GET /api/v1/fleet/can-intelligence',
            'GET /api/v1/fleet/can-operations',
            'GET /api/v1/fleet/comparison',
            'GET /api/v1/vehicles/:id/telemetry/health',
            'GET /api/v1/vehicles/:id/telemetry/trends',
            /*
             * Which of the series readings this vehicle has actually sent — what the
             * copilot's reading picker offers. Gated with the readings themselves:
             * a tenant who may not ask for a CAN series has no use for the list of
             * which ones would answer, and an ungated route would enumerate a
             * vehicle's sensor fit to somebody entitled to none of it.
             */
            'GET /api/v1/vehicles/:id/telemetry/reported-series',
        ],
        routes: ['/operations/can-intelligence', '/operations/can-operations'],
        defaultEnabled: false,
        scope: 'TENANT',
        // Engine data IS telemetry. A CAN customer therefore holds the lower tier
        // by construction rather than by a second grant somebody has to remember.
        implies: ['TELEMETRY'],
    },
    ANOMALY_DETECTION: {
        label: 'Anomaly detection',
        category: 'INTELLIGENCE',
        mandatory: false,
        description: 'Learn what is normal for each vehicle from the telemetry you already send, and raise ' +
            'an incident when today departs from it — distance, operating hours, driving time, ' +
            'idling, activity and connectivity.',
        /*
         * Empty while no route is gated yet. The registry test compares this against
         * every `@RequiresFeature` occurrence, so the first route added without being
         * listed here fails the build — which is what keeps the declaration honest
         * rather than aspirational. Detection runs in the worker and reaches no
         * endpoint until Stage 2 publishes.
         */
        gates: [
            'GET /api/v1/fleet/anomalies',
            'GET /api/v1/fleet/anomalies/findings',
            'POST /api/v1/fleet/anomalies/:incidentId/review',
            /*
             * ⚠️ The operating calendar is gated here because NOTHING ELSE READS IT
             * yet — baselines normalise working against non-working day and that is
             * its only consumer. `operating.*` is deliberately a prefix so a later key
             * inherits the parent ownership, and the day a second subsystem reads the
             * calendar this gate becomes wrong in the other direction. Revisit it with
             * the second reader rather than leaving it to be discovered.
             */
            'GET /api/v1/admin/operating-calendar',
            'PATCH /api/v1/admin/operating-calendar',
            'GET /api/v1/admin/anomaly-families',
            'PATCH /api/v1/admin/anomaly-families',
        ],
        routes: [],
        defaultEnabled: false,
        scope: 'TENANT',
        /*
         * ⚠️ **NO implication, and this is the asymmetry to read twice.**
         *
         * `CAN_TELEMETRY` implies `TELEMETRY` because engine data IS telemetry — the
         * same device, one feed. Anomaly detection CONSUMES telemetry it does not
         * produce, exactly as the tachograph does, and implying the base tier here
         * would silently grant — and BILL — positions, tracking and the fleet map to
         * every anomaly customer. That is very hard to walk back once invoiced.
         *
         * It REQUIRES `TELEMETRY` instead: a tenant holding this without it sees
         * "requires telemetry" on every family rather than an empty screen, because
         * the family catalogue declares the dependency per family (see
         * `ANOMALY_FAMILIES`).
         *
         * ⚠️ Stated as an empty list rather than omitted, and the type is what
         * insists: "this implies nothing" is a decision somebody made, and a field
         * left off would be indistinguishable from a field forgotten.
         */
        implies: [],
    },
    BULK_VEHICLE_IMPORT: {
        label: 'Bulk vehicle import',
        category: 'ADMINISTRATION',
        mandatory: false,
        description: 'Create vehicles and drivers from a CSV file, into the fleet account you are working in. ' +
            'The file is validated in full before anything is written, and re-uploading a corrected ' +
            'one is safe.',
        gates: [
            /*
             * ⚠️ BOTH, and the dry run is gated as firmly as the apply. It reads the
             * fleet's registrations, unique IDs, device ids, classes and rulesets to
             * answer per row, so an ungated validate would enumerate what a tenant
             * holds to somebody entitled to none of it — and it is the half somebody
             * would reason is "only a check". The gate is on what it READS, not on
             * what it writes.
             */
            'POST /api/v1/vehicles/imports/validate',
            'POST /api/v1/vehicles/imports/apply',
        ],
        /*
         * ⚠️ EMPTY deliberately, and NOT the same omission the gates were. `routes`
         * hides a WEB route, and the import is a control on the vehicles page
         * rather than a page of its own — a route listed here that does not exist
         * would hide nothing and read as a screen somebody had forgotten to build.
         * It gains an entry the day the import becomes its own screen.
         */
        routes: [],
        /*
         * ⚠️ **ON by default, and the default is the whole decision (D13).**
         *
         * **Ingestion never creates a vehicle** (provider-integration D12), so a
         * customer without this cannot onboard a fleet of any size at all — their
         * telemetry is refused as `UNRESOLVED_REFERENCE` until every vehicle exists,
         * and creating five hundred by dialog is not a route. Off by default, that
         * reads as the product being broken rather than as a plan boundary — which
         * is the `CAN_TELEMETRY` mistake, where *where a vehicle is* was gated
         * behind *engine diagnostics* and a plain-tracker customer could not open
         * their fleet map.
         *
         * So this is a SAFETY lever rather than a commercial one: it exists so
         * somebody can switch it OFF for a customer who misuses it, not so it can
         * be sold. ⚠️ Charging for it later means flipping this default, which is a
         * commercial decision rather than a configuration change — it would strand
         * every existing customer who had not been granted it.
         *
         * ⚠️ **`defaultEnabled` is still FALSE, and that is not a contradiction —
         * it is where D13's "on by default" actually lives.** Every feature on this
         * platform defaults off, because *a marketplace sells access and nothing is
         * included until granted*; `defaultEnabled` governs a tenant with no row at
         * all. What D13 decided is PLAN MEMBERSHIP, and this key is in EVERY plan
         * including `CORE` — so any tenant on a plan has it, and the lever is
         * revoking it from one customer rather than selling it to each.
         *
         * The contrast worth knowing: `REPORT_EXTERNAL_RECIPIENTS` defaults off AND
         * is in no plan, because it is a data-EGRESS control. This is ingress — it
         * creates rows a tenant already owns — which is materially less dangerous
         * and materially more necessary.
         */
        defaultEnabled: false,
        scope: 'TENANT',
        /*
         * ⚠️ NO implication, in either direction, and for the reason `TACHOGRAPH`
         * and `ANOMALY_DETECTION` both record: an implication silently GRANTS, and
         * bills, something nobody sold. Importing vehicles needs no telemetry tier
         * — a vehicle is a record, and it exists precisely so telemetry has
         * somewhere to land.
         *
         * ⚠️ REQUIRED rather than omittable, so "this grants nothing else" is a
         * sentence somebody wrote rather than a field they forgot.
         */
        implies: [],
    },
    TACHOGRAPH: {
        label: 'Tachograph',
        category: 'FLEET_DATA',
        mandatory: false,
        description: 'Ingest drivers’ hours from a fitted tachograph and read them per vehicle: time before ' +
            'the next break, continuous driving, daily and weekly totals, and which card is in ' +
            'which slot. ' +
            /*
             * ⚠️ **The MARKET is named, because this description is what somebody reads when
             * deciding whether to SELL it** — it is on the partner ceiling card, the partner
             * create form and the tenant features card. A tachograph is a UK and EU device and
             * these are those rules; a US fleet runs an ELD under FMCSA Hours of Service, which
             * reports different figures under different limits. Without this sentence the
             * entitlement reads as drivers'-hours support in general, and the way somebody finds
             * out otherwise is a customer in the wrong market having been granted it.
             *
             * It says what the feature SERVES, never that anybody is compliant — nothing in this
             * product claims that, and a description is not the place to start.
             */
            'Built for UK and EU tachographs: a US ELD reports different figures under different ' +
            'rules, so this does not serve that market.',
        /*
         * TWO gates, and the first is the one that matters.
         *
         * The ingestion route is already gated on `TELEMETRY` and tacho readings
         * arrive on it, so the real control is the TIER FILTER: a tenant without
         * this feature has every `TACHO` parameter dropped and counted in
         * `parametersDropped`. That is enforcement rather than a route list, which
         * is why the reading route is named here as well — a feature whose only
         * gate is invisible is one nobody can check.
         */
        gates: [
            'GET /api/v1/vehicles/:id/telemetry/tacho',
            'GET /api/v1/vehicles/:id/tachograph/activity',
            'GET /api/v1/fleet/tacho',
            'GET /api/v1/fleet/tacho/vehicles',
            /*
             * The rulesets are gated by the same feature because they change nothing without it:
             * the two alerts a ruleset moves read `TACHO`-tier counters, which a tenant without
             * this entitlement has dropped at ingestion. Ungated, the screen would accept a limit
             * and produce no alerts — inert configuration, which reads as a broken product.
             */
            'GET /api/v1/drivers-hours-rulesets',
            'POST /api/v1/drivers-hours-rulesets',
            'PATCH /api/v1/drivers-hours-rulesets/:id',
            'POST /api/v1/drivers-hours-rulesets/:id/archive',
            'POST /api/v1/drivers-hours-rulesets/:id/restore',
        ],
        routes: ['/operations/tachograph', '/vehicles/drivers-hours-rulesets'],
        defaultEnabled: false,
        scope: 'TENANT',
        /*
         * ⚠️ NO implication, and this is the asymmetry to read twice.
         *
         * `CAN_TELEMETRY` implies `TELEMETRY` because engine data IS telemetry —
         * the same device, one feed. A tachograph is independent hardware: a
         * tachograph without a tracker and a tracker without a tachograph both
         * exist in real fleets. Implying `TELEMETRY` here would silently grant —
         * and bill — positions and tracking to every tacho customer, which is very
         * hard to walk back once invoiced.
         *
         * The consequence is worth stating: a tenant holding ONLY `TACHOGRAPH` can
         * ingest and read drivers' hours and cannot open the fleet map. That is
         * correct, and it is exactly the mis-tiering that bundling position with
         * DPF soot load produced in the other direction.
         */
        implies: [],
    },
    ALERTING: {
        label: 'Outbound alerting',
        category: 'AUTOMATION',
        mandatory: false,
        description: 'Deliver alerts to people by email and Telegram, with per-person channel choice, quiet ' +
            'hours and tenant notification policy.',
        /*
         * Deliberately EMPTY, and the empty list is the decision.
         *
         * Alerting is enforced where it is used — the resolver refuses every delivery
         * for a tenant without this feature — rather than by a route guard. Gating the
         * settings routes would 403 them, so somebody whose tenant has not bought
         * alerting could not see their own preferences and could not be told WHY
         * nothing arrives. Instead the settings screen loads and says
         * "Alerting is not enabled for this tenant" against each channel.
         *
         * The registry test compares this count against `@RequiresFeature('ALERTING')`
         * occurrences, so zero here means zero guards, which is exactly right.
         */
        gates: [],
        /* Nothing is hidden: the settings page explains its own unavailability. */
        routes: [],
        defaultEnabled: false,
        scope: 'TENANT',
        implies: [],
    },
    REPORT_EXTERNAL_RECIPIENTS: {
        label: 'External report recipients',
        category: 'AUTOMATION',
        mandatory: false,
        description: 'Send a scheduled report to a named address outside the organisation — an auditor, an ' +
            'insurer, a board member. Each address proves it owns itself before it receives ' +
            'anything, and every one expires.',
        /*
         * Its own entitlement, and that is a requirement of the design rather than a
         * convenience (D23). Every other safeguard here is procedural — approval,
         * verification, expiry — and a deployment that cannot allow tenant data to
         * leave to a non-member at all needs a switch that refuses the CAPABILITY,
         * not a policy asking people not to use it.
         */
        gates: [
            'POST /api/v1/admin/reports/:id/external-recipients',
            'POST /api/v1/admin/reports/:id/external-recipients/:recipientId/verify',
            'POST /api/v1/admin/reports/:id/external-recipients/:recipientId/resend',
            'DELETE /api/v1/admin/reports/:id/external-recipients/:recipientId',
        ],
        /*
         * No route of its own. It adds a section to the existing reports screen
         * rather than a page, so listing one here would name a URL that does not
         * exist and the registry test compares this against real routes.
         */
        routes: [],
        // Off unless somebody decides otherwise. The whole point of D23's entitlement
        // is that tenant data leaving to a non-member is opt-in per customer.
        defaultEnabled: false,
        scope: 'TENANT',
        implies: [],
    },
    SCHEDULED_REPORTS: {
        label: 'Scheduled reports',
        category: 'AUTOMATION',
        mandatory: false,
        description: 'Recurring summaries of a past period — fleet operations and incidents — generated on ' +
            'a schedule in your time zone and delivered to named members as CSV, HTML or PDF.',
        /*
         * Gated like WHITE_LABEL rather than like ALERTING (design D30): a screen
         * describing something the tenant simply does not have, where "not included in
         * your plan" says everything useful.
         *
         * This list started empty while the routes did not exist, and the registry test —
         * which compares it against `@RequiresFeature` occurrences — refused the first
         * commit that added them without listing them here. That is the behaviour that
         * keeps the declaration honest rather than aspirational.
         */
        gates: [
            'GET /api/v1/admin/reports',
            'POST /api/v1/admin/reports',
            'POST /api/v1/admin/reports/:id/run',
            'PATCH /api/v1/admin/reports/:id',
            'GET /api/v1/admin/reports/:id/runs',
            'GET /api/v1/admin/reports/runs/:runId/download',
            /*
             * The external-recipient routes require BOTH entitlements, so they appear
             * in both declarations. Listing them under one alone would be a gate this
             * feature really applies and does not admit to.
             */
            'POST /api/v1/admin/reports/:id/external-recipients',
            'POST /api/v1/admin/reports/:id/external-recipients/:recipientId/verify',
            'POST /api/v1/admin/reports/:id/external-recipients/:recipientId/resend',
            'DELETE /api/v1/admin/reports/:id/external-recipients/:recipientId',
        ],
        routes: ['/admin/reports'],
        defaultEnabled: false,
        scope: 'TENANT',
        implies: [],
    },
    WHITE_LABEL: {
        label: 'White-label branding',
        category: 'ADMINISTRATION',
        mandatory: false,
        description: 'Your own name, logo, accent colour and support details on the product and on every ' +
            'notification sent to your members.',
        /*
         * Gated, unlike ALERTING — and the contrast is the point.
         *
         * The alerting settings screen loads without the entitlement because it
         * describes something the person still RECEIVES and must be able to
         * understand. A branding form describes something the tenant simply does not
         * have, and there is nothing useful it could say that "not included in your
         * plan" does not already say better.
         */
        gates: [
            'GET /api/v1/admin/branding',
            'PATCH /api/v1/admin/branding',
            // Sending as your own domain is the same capability as looking like your own
            // product, so a tenant's mail server rides the same entitlement rather than
            // splitting one feature across two.
            'GET /api/v1/admin/mail-server',
            'PATCH /api/v1/admin/mail-server',
            'POST /api/v1/admin/mail-server/verify',
        ],
        routes: ['/admin/branding'],
        defaultEnabled: false,
        scope: 'TENANT',
        implies: [],
    },
    AI_COPILOT: {
        label: 'AI Assistant',
        category: 'INTELLIGENCE',
        mandatory: false,
        /*
         * Written for the person being told they do not have it.
         *
         * `RequireFeature` renders this verbatim under "not included in your plan",
         * so it is customer copy, not a design note — the first draft explained that
         * AI is an addition and never a dependency, which is true, load-bearing, and
         * meaningless to somebody deciding whether to buy it. That reasoning is D0
         * and lives in the design document and the layering test.
         */
        description: 'Ask about your fleet in plain language and get answers backed by the records behind ' +
            'them — which vehicles need attention, why one carries the risk it does, and what ' +
            'changed since last month.',
        /*
         * Every copilot route, and the list grows with each capability rather than
         * being written ahead of one. It was empty through Phase 0 for exactly that
         * reason: a gate declared for a route nobody had written would have made the
         * entitlement look enforced while gating nothing.
         */
        gates: [
            'POST /api/v1/copilot/ask',
            'POST /api/v1/copilot/route',
            'GET /api/v1/copilot/findings',
            'GET /api/v1/copilot/findings/:id',
            'POST /api/v1/copilot/findings/:id/pin',
            'POST /api/v1/copilot/findings/:id/dismiss',
            'GET /api/v1/copilot/usage',
            'POST /api/v1/copilot/unresolved',
            'GET /api/v1/copilot/investigations',
            'PATCH /api/v1/copilot/investigations/:id',
        ],
        // The briefing landing. Blocked by `RequireFeature` and additionally
        // restricted to TENANT_ADMIN and OPERATIONS_MANAGER in v1 (R10a).
        routes: ['/copilot'],
        defaultEnabled: false,
        /*
         * TENANT, not PARENT.
         *
         * PARENT means "describes the group, and is therefore excluded from what
         * sub-tenants inherit" — a consolidated dashboard. A branch operations
         * manager uses the copilot in their own branch, so it must inherit. The
         * organisation buys it once; every branch it is granted to receives it.
         */
        scope: 'TENANT',
        implies: [],
    },
    AI_EXTERNAL_PROVIDER: {
        label: 'AI through an external provider',
        category: 'INTELLIGENCE',
        mandatory: false,
        /*
         * Customer copy, rendered verbatim by `RequireFeature` — so it says what the
         * customer is being offered rather than how the gate works.
         */
        description: 'Allow this organisation to use a model hosted outside the platform, such as OpenAI. ' +
            'Nothing is sent until the organisation also switches it on in its own settings, and ' +
            'every figure OIP computes is unchanged either way.',
        /*
         * ⚠️ DELIBERATELY EMPTY, and the empty list is the decision — the `ALERTING`
         * argument exactly.
         *
         * Enforcement is where the feature is USED: `tenantLlmSettings` folds this
         * entitlement into `allowExternalProvider`, so all four model services —
         * routing, copilot wording, incident summary and the task executor — refuse
         * together with no per-service check to forget.
         *
         * Gating the CONSENT routes would be actively wrong in two ways. A tenant
         * without the entitlement could not SEE their own consent state and could not
         * be told why nothing external happens; and once revoked they could not turn a
         * stored consent OFF, leaving a row that reads as granted and does nothing —
         * the stored no-op this platform refuses everywhere. The service refuses
         * turning it ON without the entitlement, and NAMES it.
         *
         * The registry test compares this count against `@RequiresFeature` occurrences,
         * so zero here means zero route guards, which is exactly right.
         */
        gates: [],
        /* No screen of its own: it adds a card to the existing Tenant Settings page. */
        routes: [],
        /*
         * ⚠️ OFF, and this is the one default that must never be flipped for
         * convenience. `REPORT_EXTERNAL_RECIPIENTS` makes the same argument for the same
         * reason: a deployment that may not let tenant data leave at all needs a switch
         * that refuses the CAPABILITY rather than a policy asking people not to use it.
         */
        defaultEnabled: false,
        /*
         * TENANT, so it inherits — a branch operations manager's copilot question goes
         * to the same provider as the organisation's.
         *
         * ⚠️ It implies NOTHING, in either direction, and the asymmetry with
         * `CAN_TELEMETRY` is the decision to read twice. This is permission to use a
         * DIFFERENT KIND of provider for AI somebody already has; `AI_COPILOT` is the AI
         * itself. An implication either way would silently grant — and BILL — one on the
         * strength of the other, which is very hard to walk back once invoiced. The
         * `TACHOGRAPH` argument, two features along.
         */
        scope: 'TENANT',
        implies: [],
    },
};
/**
 * Why a feature is on, kept because "included in the plan" and "switched on for
 * a trial" need different answers when a subscription changes.
 */
export const featureSourceSchema = z.enum([
    'PLAN',
    'TRIAL',
    'OVERRIDE',
    'GRANDFATHERED',
    /**
     * On because another held feature implies it (`FeatureDefinition.implies`),
     * with no row of its own.
     *
     * Its own value rather than falling back to `PLAN`, which is what the screen
     * said before this existed — and a tenant on a plan that does not list the
     * feature would have been told it came from one. An implied entitlement also
     * cannot be revoked on its own (the write refuses it, naming the implier), so
     * the reader needs to know why the control will not do what it looks like it
     * does.
     */
    'IMPLIED',
    /**
     * A row exists and is switched on, but the white-label partner that owns this
     * company does not hold the feature — so it is OFF.
     *
     * The inverse of `IMPLIED`, and it exists for the same reason: **a row that is
     * present and inert must never render as one that is working.** Without it
     * the screen would show the feature granted while every request refused it,
     * which is the "stored no-op" the entitlement model refuses everywhere else.
     *
     * It happens when a partner's ceiling is REVOKED after a company was granted
     * something — the grant is refused at write time, so this is only ever
     * reached by a later revocation, which is exactly the case that write-time
     * refusal alone cannot cover.
     */
    'ABOVE_CEILING',
]);
export const tenantFeatureSchema = z.object({
    feature: featureKeySchema,
    enabled: z.boolean(),
    source: featureSourceSchema,
    note: z.string().nullable(),
    /**
     * When a time-limited grant stops applying; NULL means it does not expire.
     *
     * Present on the DTO even when the row is disabled, because "this lapsed on
     * 12 August" and "somebody switched this off" are different facts and the
     * screen has to be able to say which.
     */
    expiresAt: z.string().datetime().nullable(),
    updatedAt: z.string().datetime(),
});
export const tenantFeaturesResponseSchema = z.object({
    tenantId: z.string().uuid(),
    features: z.array(tenantFeatureSchema),
});
export const updateTenantFeatureSchema = z
    .object({
    feature: featureKeySchema,
    enabled: z.boolean(),
    source: featureSourceSchema.default('OVERRIDE'),
    /** Why, for the audit trail — "trial to 31 Aug", "downgraded plan". */
    note: z.string().trim().max(300).optional(),
    /**
     * When the grant lapses. Omit, or send null, for one that does not.
     *
     * Any source may carry one — "override until the migration finishes" is a
     * real thing to want — but see the refinement below for TRIAL.
     */
    expiresAt: z.string().datetime().nullable().optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (!v.enabled) {
        // Revoking with an expiry date is incoherent: the row is off now, so
        // there is nothing for a future date to do. Rejecting it beats storing a
        // date that will never fire and will be read later as meaningful.
        if (v.expiresAt != null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['expiresAt'],
                message: 'An expiry date cannot be set while disabling a feature.',
            });
        }
        return;
    }
    // A trial with no end date is a permanent grant wearing a trial's label,
    // which is the exact defect this field exists to close. Refusing it at the
    // edge is cheaper than a quarterly report that finds twelve of them.
    if (v.source === 'TRIAL' && v.expiresAt == null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['expiresAt'],
            message: 'A trial must have an expiry date.',
        });
    }
    if (v.expiresAt != null && Date.parse(v.expiresAt) <= Date.now()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['expiresAt'],
            message: 'The expiry date must be in the future.',
        });
    }
});
/**
 * The error code a gated route returns.
 *
 * Distinct from a role refusal on purpose: "your plan does not include this"
 * and "you are not permitted to do this" need different things from the reader.
 * One is answered by a purchase, the other by an administrator.
 */
export const FEATURE_NOT_ENABLED = 'FEATURE_NOT_ENABLED';
// ── plans ───────────────────────────────────────────────────────────────────
/**
 * Named bundles of features, as sold.
 *
 * Declared here rather than stored in a table, for the same reason `FEATURES`
 * is: a plan changes at release cadence, not daily, and a registry forces the
 * decision at declaration where a table would let one be created with nobody
 * asked what it includes. When AWS Marketplace product codes are wired up they
 * become a field on the plan, mapping a purchase onto a set of entitlements.
 *
 * A plan is a STARTING POINT, not the source of truth. `TenantFeature` rows are
 * what the guard reads, and selecting a plan writes them with `source = PLAN`.
 * That is what allows a trial or a one-off grant to sit alongside a plan without
 * either being mistaken for the other — and what makes "why does this tenant
 * have CAN?" answerable.
 */
export const PLAN_KEYS = ['CORE', 'FLEET_INTELLIGENCE'];
export const planKeySchema = z.enum(PLAN_KEYS);
/*
 * AI_COPILOT is deliberately in NEITHER plan.
 *
 * An absent key here looks like an oversight, so: it is sold as an addition,
 * granted directly rather than by selecting a plan, and D0 is what makes that
 * saleable — a customer on either plan gets the same decisions, numbers and
 * evidence without it. Folding it into Fleet Intelligence would price the
 * platform's own output behind the model.
 */
export const PLANS = {
    CORE: {
        label: 'Core',
        description: 'Incidents, recommendations, drivers and fleet administration. No CAN telemetry.',
        // Alerting is in every plan: being TOLD something happened is not the upsell —
        // the metered channels (SMS, WhatsApp) are, and those are gated separately.
        //
        // White-label likewise: a deployment may be white-labelled as a whole rather
        // than sold per tenant, so gating it to the larger plan would make the
        // simplest configuration impossible to express.
        // Scheduled reports likewise: the launch templates summarise incidents and
        // recommendations, which every plan already has. Gating a weekly summary of data a
        // tenant owns to the larger plan would be selling them their own numbers back.
        // Reports over CAN data are gated per TEMPLATE by CAN_TELEMETRY instead.
        // Bulk import is in EVERY plan (D13): ingestion never creates a vehicle, so
        // a customer without it cannot onboard a fleet of any size — that is not a
        // plan boundary, it reads as the product being broken.
        features: ['ALERTING', 'WHITE_LABEL', 'SCHEDULED_REPORTS', 'BULK_VEHICLE_IMPORT'],
    },
    FLEET_INTELLIGENCE: {
        label: 'Fleet Intelligence',
        description: 'Core, plus CAN telemetry and everything derived from it.',
        features: [
            'CAN_TELEMETRY',
            'ALERTING',
            'WHITE_LABEL',
            'SCHEDULED_REPORTS',
            'BULK_VEHICLE_IMPORT',
        ],
    },
};
/**
 * Why a tenant may have no plan.
 *
 * Tenants that existed before plans were introduced carry NULL rather than being
 * assigned one. Guessing which plan they had bought would be inventing a
 * commercial fact from a technical one — their features came from
 * `GRANDFATHERED` rows, and that is what the UI says.
 */
export const NO_PLAN_LABEL = 'No plan (pre-dates plans)';
/**
 * How many days ahead of expiry a trial is called out as ending soon.
 *
 * A single shared number so the platform screen and any later notification
 * agree; two definitions of "expiring soon" is how a customer gets warned twice
 * with different dates.
 */
export const TRIAL_EXPIRY_WARNING_DAYS = 7;
/** Whether a grant has lapsed, applied identically wherever the question is asked. */
export function isExpired(expiresAt, now = new Date()) {
    // `== null` catches undefined too: a DTO built before the field existed, or a
    // fixture that omits it, must read as "does not expire" rather than falling
    // through to Date.parse(undefined) and relying on NaN comparing false.
    return expiresAt != null && Date.parse(expiresAt) <= now.getTime();
}
//# sourceMappingURL=features.js.map